import { useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  IconButton,
} from "@mui/material";
import { Upload, FileDown, FileUp, X, HelpCircle } from "lucide-react";
import Papa from "papaparse";

const CSV_HEADERS = [
  "Competitor Name",
  "Website URL",
  "Industry",
  "Market Position",
  "Ad Spend/Budget",
  "Target Audience",
  "Product Offerings",
  "Pricing Strategy",
  "Unique Selling Proposition (USP)",
  "Strengths",
  "Weaknesses",
  "Additional Notes",
];

const CompetitorDataDialog = ({
  open,
  onClose,
  onSubmit,
  loading,
  error,
}: any) => {
  const [modelAPIKey, setModelAPIKey] = useState("");
  const [icpId, setIcpId] = useState("");
  const [competitorData, setCompetitorData] = useState("");
  const [fileError, setFileError] = useState("");
  const [showHelp, setShowHelp] = useState(false);

  const downloadTemplate = useCallback(() => {
    // Create CSV content with just the headers
    const csv = Papa.unparse([CSV_HEADERS]);

    // Create and trigger download
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = "competitor-analysis-template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  const handleFileUpload = useCallback((event: any) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset errors
    setFileError("");

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          setFileError("Error parsing CSV file. Please check the format.");
          return;
        }

        // Validate headers
        const headers = Object.keys(results.data[0] || {});
        const missingHeaders = CSV_HEADERS.filter((h) => !headers.includes(h));

        if (missingHeaders.length > 0) {
          setFileError(
            `Missing required columns: ${missingHeaders.join(", ")}`
          );
          return;
        }

        // Convert parsed data back to CSV format
        setCompetitorData(Papa.unparse(results.data));
      },
      error: (error) => {
        setFileError(`Error reading file: ${error.message}`);
      },
    });
  }, []);

  const handleSubmit = () => {
    onSubmit({
      icpId,
      modelAPIKey,
      competitorData,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          overflow: "hidden",
        },
      }}
    >
      {/* Custom header with gradient background */}
      <Box
        sx={{
          position: "relative",
          background: "linear-gradient(to right, #E11D48, #BE123C)",
          padding: "20px 24px",
          color: "white",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.2)",
            },
          }}
        >
          <X size={18} />
        </IconButton>

        <Typography variant="h5" sx={{ fontWeight: 600, fontSize: "1.25rem" }}>
          Generate Competitive Analysis
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
          Upload competitor data to generate detailed market insights
        </Typography>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ mt: 2, mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              Competitor Data
            </Typography>
            <IconButton size="small" onClick={() => setShowHelp(!showHelp)}>
              <HelpCircle size={16} color="#E11D48" />
            </IconButton>
          </Box>

          {showHelp && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                backgroundColor: "#FEE2E2",
                borderRadius: "8px",
                border: "1px solid #FECACA",
              }}
            >
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>CSV Format Instructions:</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Upload a CSV with competitor information including company
                details, market position, offerings, strengths, and weaknesses.
                Download the template below to see the required format.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                For best results, include at least 3-5 competitors with detailed
                information.
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              mb: 3,
            }}
          >
            <Button
              startIcon={<FileDown className="h-4 w-4" />}
              variant="outlined"
              size="medium"
              onClick={downloadTemplate}
              sx={{
                borderRadius: "8px",
                background: "linear-gradient(to right, #E11D48, #BE123C)",
                textTransform: "none",
                fontWeight: 500,
                color: "white",
                "&:hover": {
                  background: "linear-gradient(to right, #BE123C, #9F1239)",
                },
              }}
            >
              Download Template
            </Button>
            <Button
              component="label"
              startIcon={<FileUp className="h-4 w-4" />}
              variant="contained"
              size="medium"
              sx={{
                borderRadius: "8px",
                background: "linear-gradient(to right, #E11D48, #BE123C)",
                textTransform: "none",
                fontWeight: 500,
                "&:hover": {
                  background: "linear-gradient(to right, #BE123C, #9F1239)",
                },
              }}
            >
              Upload CSV
              <input
                type="file"
                hidden
                accept=".csv"
                onChange={handleFileUpload}
              />
            </Button>
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: "8px",
                "& .MuiAlert-icon": {
                  color: "#EF4444",
                },
              }}
            >
              {error}
            </Alert>
          )}

          {fileError && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: "8px",
                "& .MuiAlert-icon": {
                  color: "#EF4444",
                },
              }}
            >
              {fileError}
            </Alert>
          )}
        </Box>
        <TextField
          fullWidth
          label="Model API Key"
          value={modelAPIKey}
          onChange={(e) => setModelAPIKey(e.target.value)}
          placeholder="Enter your Model API Key"
          helperText="This should be available within your selected model's hosted platform/console settings"
          sx={{
            mb: 3,
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
            },
          }}
        />

        <TextField
          fullWidth
          label="ICP ID"
          value={icpId}
          onChange={(e) => setIcpId(e.target.value)}
          placeholder="Enter your ICP ID"
          helperText="The ideal customer profile (ICP) ID is required to reference during the analysis for better results"
          sx={{
            mb: 3,
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
            },
          }}
        />

        <TextField
          multiline
          rows={6}
          fullWidth
          label="Competitor Data (CSV)"
          placeholder="Upload a CSV file or paste competitor data here..."
          value={competitorData}
          onChange={(e) => setCompetitorData(e.target.value)}
          sx={{
            mb: 3,
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
            },
          }}
          helperText="CSV format: one competitor per row with all required fields"
        />
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 0,
          display: "flex",
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            borderRadius: "8px",
            color: "#374151",
            fontWeight: 500,
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#F9FAFB",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!modelAPIKey || !icpId || !competitorData || loading}
          startIcon={<Upload className="h-4 w-4" />}
          sx={{
            borderRadius: "8px",
            background: "linear-gradient(to right, #E11D48, #BE123C)",
            textTransform: "none",
            fontWeight: 500,
            boxShadow: "0 4px 6px -1px rgba(225, 29, 72, 0.2)",
            "&:hover": {
              background: "linear-gradient(to right, #BE123C, #9F1239)",
            },
            "&.Mui-disabled": {
              background: "#E5E7EB",
              color: "#9CA3AF",
            },
          }}
        >
          {loading ? "Generating..." : "Generate Analysis"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompetitorDataDialog;
